// _public_movil/MovilLayout.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FaInfoCircle, 
  FaMap, 
  FaClipboardList, 
  FaUsers, 
  FaEllipsisH,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaWhatsapp,
  FaChevronUp,
  FaChevronDown,
  FaRoad,
  FaClock,
  FaRunning,
  FaShoppingBag,  
  FaFlag,      
  FaFlagCheckered,   
  FaTrophy,          
  FaArrowRight 
} from 'react-icons/fa';
import { LuMedal } from 'react-icons/lu';
import api from '../../../api/axios';
import { useBanner } from '../_shared/hooks/useBanner';
import { useColors } from '../_shared/hooks/useColors';
import { useInfo } from '../_shared/hooks/useInfo';
import { useRoutes } from '../_shared/hooks/useRoutes';
import { useCompetitors } from '../_shared/hooks/useCompetitors';
import { useLive } from '../_shared/hooks/useLive';
import { usePackages } from '../_shared/hooks/usePackages';
import { useSponsors } from '../_shared/hooks/useSponsors';
import { useGallery } from '../_shared/hooks/useGallery';
import { useNavbar } from '../_shared/hooks/useNavbar';
import { useEventData  } from '../_shared/hooks/useEventCountdown';
import EventCountdown from '../_shared/utils/EventCountdown';
import MovilFormulario from '../../_forms/_public_movil/MovilFormulario';
import './MovilLayout.css';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';


// Configuración de iconos de Leaflet (para evitar errores)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MovilLayout = ({ slug: propSlug, viewMode = false, templateData: initialData }) => {
  const { slug: urlSlug } = useParams();
  const slug = propSlug || urlSlug;

  // ==========================================
  // ESTADOS
  // ==========================================
  const [templateData, setTemplateData] = useState(initialData || null);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [activeTab, setActiveTab] = useState('info');
  const [searchTerm, setSearchTerm] = useState('');
  const [competitorViewMode, setCompetitorViewMode] = useState('general');
  const [showFormulario, setShowFormulario] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  // _public_movil/MovilLayout.js - Agregar estados
const [currentPackageIndex, setCurrentPackageIndex] = useState(0);
const [touchStartX, setTouchStartX] = useState(0);
const [touchEndX, setTouchEndX] = useState(0);
const [showInfoModal, setShowInfoModal] = useState(false);
const [selectedAddons, setSelectedAddons] = useState({});
const [isSwiping, setIsSwiping] = useState(false);
const [touchStartTime, setTouchStartTime] = useState(0);
const [activeRouteIndex, setActiveRouteIndex] = useState(0);

// Funciones para navegar paquetes
const nextPackage = () => {
  if (packages.length > 0) {
    setCurrentPackageIndex((prev) => (prev + 1) % packages.length);
  }
};

const prevPackage = () => {
  if (packages.length > 0) {
    setCurrentPackageIndex((prev) => (prev - 1 + packages.length) % packages.length);
  }
};

// Manejar gestos táctiles
const handleTouchStart = (e) => {
  setTouchStartX(e.touches[0].clientX);
  setTouchStartTime(Date.now());
  setIsSwiping(false);
};

const handleTouchMove = (e) => {
  const currentX = e.touches[0].clientX;
  const diff = touchStartX - currentX;
  if (Math.abs(diff) > 10) {
    setIsSwiping(true);
  }
  setTouchEndX(currentX);
};

const handleTouchEnd = (e) => {
  // Si no hubo un deslizamiento real en el eje X, no hacemos nada (es un click/touch simple)
  if (!isSwiping) {
    return;
  }

  const diff = touchStartX - touchEndX;
  const timeDiff = Date.now() - touchStartTime;
  
  // Si fue un swipe rápido (menos de 300ms) o la distancia es > 30px
  if (timeDiff < 300 && Math.abs(diff) > 30) {
    if (diff > 30) {
      nextPackage();
    } else if (diff < -30) {
      prevPackage();
    }
  }
  
  // Resetear de forma segura
  setTimeout(() => {
    setIsSwiping(false);
  }, 100);
};

// Función para toggle de addon
const toggleAddon = (pkgId, addonId) => {
  setSelectedAddons(prev => {
    const pkgAddons = prev[pkgId] || {};
    const currentQty = parseInt(pkgAddons[addonId]) || 0;
    
    // Encontramos el paquete para saber el número de competidores
    const pkg = packages.find(p => String(p.id_package || p.id) === String(pkgId));
    const numCompetitors = pkg ? (parseInt(pkg.number_of_competitors) || 1) : 1;
    
    // Si ya tiene cantidad > 0, lo quitamos (0). Si es 0, lo ponemos al máximo (numCompetitors)
    const newQty = currentQty > 0 ? 0 : numCompetitors;
    
    const newPkgAddons = {
      ...pkgAddons,
      [addonId]: newQty
    };
    
    // Si no hay addons seleccionados (todos 0 o false), eliminamos la entrada del paquete
    if (Object.values(newPkgAddons).every(v => parseInt(v) === 0 || v === false)) {
      const newState = { ...prev };
      delete newState[pkgId];
      return newState;
    }
    
    return {
      ...prev,
      [pkgId]: newPkgAddons
    };
  });
};

// Función para calcular precio POR PERSONA con addons
const calcularPrecioConAddons = (pkg) => {
  const baseAmount = parseFloat(pkg.amount) || 0;
  const pkgAddons = selectedAddons[pkg.id_package || pkg.id] || {};
  
  let extraPerPerson = 0;
  if (pkg.addons) {
    pkg.addons.forEach((addon, idx) => {
      const addonId = addon.id || idx;
      const qty = parseInt(pkgAddons[addonId]) || 0;
      if (qty > 0 && addon.extra_amount) {
        // 🔥 Sumamos el precio UNITARIO del addon (por persona), no el total
        extraPerPerson += parseFloat(addon.extra_amount);
      }
    });
  }
  // 🔥 Devuelve el precio POR PERSONA (base + addons por persona)
  return (baseAmount + extraPerPerson).toFixed(2);
};

const formatSmart = (amount, currency = '') => {
  const num = parseFloat(amount);
  if (isNaN(num)) return currency ? `0 ${currency}` : '0';
  // Si tiene decimales significativos, mostrarlos. Si no, solo entero
  const formatted = num % 1 !== 0 ? num.toFixed(2) : num.toFixed(0);
  return currency ? `${formatted} ${currency}` : formatted;
};

  // ==========================================
  // HOOKS
  // ==========================================
  const { colors } = useColors(slug);
  const { telefono } = useNavbar(slug);

  const banner = useBanner({
    initialLogo: templateData?.banner?.logo || '',
    initialText: templateData?.banner?.text || '',
    initialBackground: templateData?.banner?.background || '',
    initialButtonText: 'Inscríbete',
    slug,
    isEditable: false,
  });

  const { content: infoContent } = useInfo(slug);
  const { routes } = useRoutes(slug);
  const { 
    competitors, 
    isLoading: isLoadingCompetitors, 
    error: competitorsError,
    isListOcultaPublic,
    categoryColumnName,
    normalizeCategory,
    capitalizeCategory,
    refreshCompetitors,
    getCompetitorCategory,
    getCompetitorDorsal,
    visibleColumns,
  } = useCompetitors(slug, false);

  // 🔍 Detectar columnas dinámicas de nombre/apellido/club
const colKeys = useMemo(() => {
  const sample = competitors[0] || {};
  const keys = Object.keys(sample);
  const nombreCol =
    keys.find((k) => /nombre|name/i.test(k) && !/apellido|lastname/i.test(k)) || null;
  const apellidoCol =
    keys.find((k) => /apellido|lastname/i.test(k)) || null;
  const clubCol =
    keys.find((k) => /club|equipo|team/i.test(k)) || null;
  return { nombreCol, apellidoCol, clubCol };
}, [competitors]);

// ✅ Helper para verificar si un campo es visible
const isVisible = (colName) => {
  if (!visibleColumns || visibleColumns.length === 0) return true;
  return visibleColumns.some(col => 
    col.toLowerCase() === colName.toLowerCase()
  );
};

// ✅ Identificar qué columnas mostrar (sin || true)
const showDorsal = isVisible('dorsal') || isVisible('bib') || isVisible('numero');
const showName =
  isVisible('nombre') ||
  isVisible('nombres') ||
  isVisible('apellido') ||
  isVisible('apellidos') ||
  isVisible('nombre_ciclista_1') ||
  isVisible('apellido_ciclista_1') ||
  isVisible('nombre_ciclista_2') ||
  isVisible('apellido_ciclista_2');
const showCategory = isVisible('categoria') || isVisible('category') || isVisible('paquete') || isVisible('categorias');
const showClub = isVisible('club') || isVisible('equipo') || isVisible('team') || isVisible('clubequipo');

// Si no hay columnas visibles configuradas, mostrar todo por defecto
const hasVisibleConfig = visibleColumns && visibleColumns.length > 0;

// Fallback: si no hay configuración, mostrar todo
const finalShowDorsal = hasVisibleConfig ? showDorsal : true;
const finalShowName = hasVisibleConfig ? showName : true;
const finalShowClub = hasVisibleConfig ? showClub : true;
const finalShowCategory = hasVisibleConfig ? showCategory : true;
 
  const { packages } = usePackages(slug);
  const { sponsors } = useSponsors(slug);
  const { images } = useGallery(slug);

  // 🆕 Calcular fecha de oferta formateada
const offerDateFormatted = useMemo(() => {
  if (!packages || packages.length === 0) return null;
  
  // Buscar el primer paquete que tenga endDate
  const firstPackageWithOffer = packages.find(pkg => pkg.endDate);
  if (!firstPackageWithOffer?.endDate) return null;
  
  try {
    const date = new Date(firstPackageWithOffer.endDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  } catch (error) {
    console.error('Error formateando fecha de oferta:', error);
    return null;
  }
}, [packages]);

  // ==========================================
  // COUNTDOWN
  // ==========================================
  const { eventData, loading: countdownLoading } = useEventData(slug);


  // ==========================================
  // CARGAR DATOS
  // ==========================================
  useEffect(() => {
    if (!initialData && slug) {
      const fetchData = async () => {
        try {
          const response = await api.get(`/admin/template/${slug}`);
          setTemplateData(response.data);
          setIsLoading(false);
        } catch (error) {
          console.error('Error:', error);
          setIsLoading(false);
        }
      };
      fetchData();
    } else if (initialData) {
      setIsLoading(false);
    }
  }, [slug, initialData]);

  const handleTabChange = useCallback(async (tabId) => {
    setActiveTab(tabId);
    
    // Si es la tab de participantes, refrescar los datos
    if (tabId === 'participantes' && refreshCompetitors) {
      try {
        await refreshCompetitors();
        
      } catch (err) {
        console.error('❌ Error al actualizar participantes:', err);
      }
    }
  }, [refreshCompetitors]);

   // Payment methods
   const paymentMethods = [
    "/icons/visa.svg",
    "/icons/mastercard.svg",
    "/icons/qr.svg"
  ];

  
  // ==========================================
  // TABS CONFIG
  // ==========================================
  const tabs = [
    { id: 'info', icon: <FaInfoCircle />, label: 'Info' },
    { id: 'rutas', icon: <FaMap />, label: 'Rutas' },
    { id: 'inscripcion', icon: <FaClipboardList />, label: 'Inscripción' },
    { id: 'participantes', icon: <FaUsers />, label: 'Participantes' },
    { id: 'mas', icon: <FaEllipsisH />, label: 'Más' },
  ];

// ==========================================
// RENDER: INFO TAB (con carrusel en cascada COMPLETO)
// ==========================================
const renderInfoTab = () => {
  // Obtener paquetes para el carrusel en cascada
  const getCarouselPackages = () => {
    if (packages.length === 0) return [];
    const total = packages.length;
    const result = [];
    
    const positions = [-1, 0, 1];
    
    positions.forEach(position => {
      const index = (currentPackageIndex + position + total) % total;
      
      let translateX = 0;
      let scale = 1;
      let opacity = 1;
      let zIndex = 10;
      
      switch (position) {
        case -1:
          translateX = -30;
          scale = 0.85;
          opacity = 0.5;
          zIndex = 5;
          break;
        case 0:
          translateX = 0;
          scale = 1;
          opacity = 1;
          zIndex = 10;
          break;
        case 1:
          translateX = 30;
          scale = 0.85;
          opacity = 0.5;
          zIndex = 5;
          break;
        default:
          break;
      }
      
      result.push({
        ...packages[index],
        position,
        isActive: position === 0,
        translateX,
        scale,
        opacity,
        zIndex,
        realIndex: index
      });
    });
    
    return result;
  };

  const carouselPackages = getCarouselPackages();

  // Helper para obtener etiqueta de competidor
  const getCompetitorLabel = (numCompetitors) => {
    switch(parseInt(numCompetitors)) {
      case 1: return 'Individual';
      case 2: return 'Duplas';
      case 3: return 'Tríos';
      case 4: return 'Equipos de 4';
      case 5: return 'Equipos de 5';
      case 6: return 'Equipos de 6';
      default: return `Equipos de ${numCompetitors}`;
    }
  };

  // ==========================================
  // ✅ RETURN CON EL MODAL DENTRO
  // ==========================================
  return (
    <div className="movil-tab-content">
      
      {/* ==========================================
          BANNER
          ========================================== */}
      <div className="movil-banner-wrapper">
        <div 
          className="movil-banner-cover"
          style={{ 
            backgroundImage: `url(${banner.backgroundPreview})`,
            backgroundPosition: 'center 30%',
            backgroundSize: '100% 100%;'
          }}
        >
          <div className="movil-banner-overlay">
            <div className="movil-banner-info">
              {banner.logoPreview && (
                <img src={banner.logoPreview} alt="Logo" className="movil-banner-logo" />
              )}
              <h1 className="movil-banner-text">{banner.text}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* ==========================================
          Ubicación y Fecha
          ========================================== */}
      {/* 🔥 METADATA DEL EVENTO - Móvil (con diseño original) */}
      <div className="movil-meta-section">
        <div className="movil-meta-item">
          <FaMapMarkerAlt 
            className="movil-meta-icon" 
            style={{ color: colors?.primaryColor }} 
          />
          <span>
            {eventData?.locationName || 
             eventData?.locationCity || 
             'Ubicación por confirmar'}
          </span>
        </div>
        <div className="movil-meta-item">
          <FaCalendarAlt 
            className="movil-meta-icon" 
            style={{ color: colors?.primaryColor }} 
          />
          <span>
            {eventData?.endDate 
              ? new Date(eventData.endDate).toLocaleDateString('es-ES', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })
              : 'Fecha por confirmar'}
          </span>
        </div>
      </div>

      {/* ==========================================
          Countdown
          ========================================== */}
<div className="movil-countdown-box">
  <span className="movil-countdown-label">FALTAN</span>
  <EventCountdown
    endDate={eventData?.endDate}
    loading={countdownLoading}
    variant="movil"
  />
</div>

      {/* ==========================================
          SLIDER DE SPONSORS / AUSPICIADORES (MÓVIL)
          ========================================== */}
      {sponsors && sponsors.length > 0 && (
        <div className="movil-sponsors-banner-section">
          <span className="movil-sponsors-title">AUSPICIADORES OFICIALES</span>
          <div className="movil-sponsors-slider-track">
            <div className="movil-sponsors-slide-wrapper">
              {/* Duplicamos la lista para crear un loop infinito fluido en CSS */}
              {[...sponsors, ...sponsors].map((sponsor, idx) => (
                <div key={idx} className="movil-sponsor-item">
                  <img src={sponsor} alt={`Sponsor ${idx + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          BOTÓN VER CONVOCATORIA
          ========================================== */}
      <button 
        className="movil-info-btn"
        style={{
          margin: '15px auto 15px',
          padding: '12px 20px',
          borderRadius: '30px',
          width: '80%',
          fontSize: '1rem',
          fontWeight: '700',
          cursor: 'pointer',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          border: '2px solid transparent',
          background: 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(135deg, #bc00dd, #00c6fb) border-box',
        }}
        onClick={() => setShowInfoModal(true)}
      >
        <FaInfoCircle style={{ color: '#bc00dd', fontSize: '1.1rem', flexShrink: 0 }} />
        <span style={{
          backgroundImage: 'linear-gradient(135deg, #bc00dd, #00c6fb)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'inline-block'
        }}>
          Ver Convocatoria
        </span>
      </button>

{/* ==========================================
          ✅ MODAL INFO - DENTRO DEL RETURN
          ========================================== */}
      {showInfoModal && (
        <div className="movil-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="movil-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="movil-modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            <h3 className="movil-modal-title">📋 Convocatoria del Evento</h3>
            
            <div className="movil-modal-meta">
              <div className="movil-modal-meta-item">
                <FaMapMarkerAlt style={{ color: colors?.primaryColor || '#9370DB' }} />
                <span>{eventData?.locationName || eventData?.locationCity || 'Ubicación no definida'}</span>
              </div>
              <div className="movil-modal-meta-item">
                <FaCalendarAlt style={{ color: colors?.primaryColor || '#9370DB' }} />
                <span>
                  {eventData?.endDate 
                    ? new Date(eventData.endDate).toLocaleDateString('es-ES', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })
                    : 'Fecha no definida'}
                </span>
              </div>
            </div>
            
            <div className="movil-modal-info-content">
              {(() => {
                const infoData = templateData?.info;
                if (!infoData) return <p>No hay información disponible.</p>;

                let type = 'html';
                let content = '';

                if (typeof infoData === 'string') {
                  try {
                    const parsed = JSON.parse(infoData);
                    type = parsed.type || 'html';
                    content = parsed.content || '';
                  } catch (e) {
                    content = infoData;
                  }
                } else if (typeof infoData === 'object') {
                  type = infoData.type || 'html';
                  content = infoData.content || '';
                }

                const baseUrl = process.env.REACT_APP_UPLOADS_INFO?.replace(/\/$/, '') || 'http://localhost:5000';

                if (type === 'pdf') {
                  const pdfUrl = `${baseUrl}${content}`;
              
                  return (
                    <div style={{ width: '100%', textAlign: 'center' }}>
                      <a 
                        href={pdfUrl} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="movil-info-btn"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 18px',
                          marginBottom: '12px',
                          fontSize: '0.9rem',
                          textDecoration: 'none'
                        }}
                      >
                        📄 Abrir PDF a pantalla completa
                      </a>
                      
                      <div className="movil-pdf-wrapper">
                        <iframe
                          src={pdfUrl}
                          className="movil-pdf-iframe"
                          title="Convocatoria PDF"
                        />
                      </div>
                    </div>
                  );
                }
              
                return <div dangerouslySetInnerHTML={{ __html: content }} />;
              })()}
            </div>
          </div>
        </div>
      )}
{/* ==========================================
   PAQUETES - GRILLA 2 COLUMNAS (MÓVIL)
   ========================================== */}
{packages.length > 0 && (
  <div className="movil-packages-grid-wrapper">
    <h3 className="movil-packages-grid-title">
      <span style={{ color: 'black' }}>ELIGE TU PAQUETE </span>
      <span style={{
        background: `linear-gradient(135deg, #bc00dd, #00c6fb)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        INSCRÍBETE
      </span>
    </h3>

    {/* 🆕 Subtítulo con fecha límite de oferta */}
{offerDateFormatted && (
  <p style={{
    textAlign: 'center',
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#6b7280',
    marginTop: '-5px',
    marginBottom: '15px',
    padding: '0 16px'
  }}>
    Inscripciones hasta el{' '}
    <span style={{ 
      color: '#bc00dd', 
      fontWeight: '700' 
    }}>
      {offerDateFormatted}
    </span>
  </p>
)}
    
    <div className={`movil-packages-grid ${packages.length === 1 ? 'one-item' : ''} ${packages.length === 3 ? 'three-items' : ''}`}>
      {packages.map((pkg, index) => {
        const isClosed = pkg.is_closed;
        const totalAmount = calcularPrecioConAddons(pkg);
        const isTeamPackage = pkg.number_of_competitors > 1;
        
        return (
          <div 
            key={pkg.id_package || index} 
            className={`movil-package-card-grid ${isClosed ? 'movil-package-card--closed' : ''}`}
          >
            {/* Overlay si está cerrado */}
            {isClosed && (
              <div className="movil-package-overlay">
                <span className="movil-package-overlay-text">AGOTADO</span>
              </div>
            )}

            {/* BADGE */}
            <div className="movil-package-badge-grid" style={{ 
              background: isClosed 
                ? '#6c757d' 
                : `linear-gradient(135deg, ${colors?.primaryColor || '#bc00dd'}, ${colors?.secondaryColor || '#00c6fb'})` 
            }}>
              {getCompetitorLabel(pkg.number_of_competitors)}
            </div>

            {/* IMAGEN con opacidad si está cerrado */}
            <div className="movil-package-image-grid">
              {pkg.image ? (
                <img src={pkg.image} alt={pkg.title} style={{ opacity: isClosed ? 0.5 : 1 }} />
              ) : (
                <div className="movil-package-image-placeholder-grid">
                  <LuMedal style={{ fontSize: '2.5rem', color: isClosed ? '#6c757d' : (colors?.primaryColor || '#9370DB') }} />
                </div>
              )}
            </div>

            {/* TÍTULO */}
            <h4 className="movil-package-name-grid">{pkg.title}</h4>

            {/* DESCRIPCIÓN */}
            <p className="movil-package-desc-grid">{pkg.description}</p>

            {/* PRECIO con tachado si está cerrado */}
            <div className="movil-package-price-grid">
              <div className="movil-price-main-grid">
                <span className="movil-price-currency-grid">{pkg.currency}</span>
                <span className={`movil-price-amount-grid ${isClosed ? 'movil-price-amount--closed' : ''}`}>
                  {formatSmart(totalAmount * pkg.number_of_competitors)}
                </span>
              </div>

              {isTeamPackage && (
                <div className="movil-price-per-runner-grid">
                  <span className="movil-price-per-runner-label-grid">
                    {formatSmart(totalAmount)} {pkg.currency}
                  </span>
                  <span className="movil-price-per-runner-text-grid">por corredor</span>
                </div>
              )}

              {pkg.conversion_rate && pkg.amount && (
                <span className="movil-price-usd-grid">
                  ≈ {(parseFloat(totalAmount * pkg.number_of_competitors) / pkg.conversion_rate).toFixed(2)} USD
                </span>
              )}

              {/* Badge de AGOTADO en móvil */}
              {isClosed && (
                <div className="movil-package-sold-out-badge">
                  <span>🔒 AGOTADO</span>
                </div>
              )}
            </div>



            {/* ADDONS deshabilitados si está cerrado */}
{pkg.addons && pkg.addons.length > 0 && (
  <div className="movil-package-addons-grid">
    <span className="movil-addons-title-grid">Extras (opcionales):</span>
    {pkg.addons.map((addon, idx) => {
      const addonId = addon.id || idx;
      const pkgAddons = selectedAddons[pkg.id_package || pkg.id] || {};
      const currentQty = parseInt(pkgAddons[addonId]) || 0;
      const isChecked = currentQty > 0;
      const numCompetitors = parseInt(pkg.number_of_competitors) || 1;
      const unitPrice = parseFloat(addon.extra_amount || 0);
      
      // 🔥 Si está marcado, mostramos el total. Si no, mostramos el unitario.
      const displayPrice = isChecked ? (unitPrice * numCompetitors) : unitPrice;
      
      return (
        <label key={idx} className={`movil-addon-item-grid ${isClosed ? 'movil-addon-item--disabled' : ''}`}>
          <input 
            type="checkbox"
            checked={isChecked}
            onChange={() => !isClosed && toggleAddon(pkg.id_package || pkg.id, addonId)}
            disabled={isClosed}
          />
          <span>{addon.label}</span>
          <span className="movil-addon-price-grid">
            +{formatSmart(displayPrice, pkg.currency)}
            {isChecked && numCompetitors > 1 && (
              <span className="movil-addon-qty-badge" style={{ 
                fontSize: '0.7rem', 
                background: '#bc00dd', 
                color: 'white', 
                padding: '1px 5px', 
                borderRadius: '8px', 
                marginLeft: '4px',
                fontWeight: '700'
              }}>x{numCompetitors}</span>
            )}
            {!isChecked && numCompetitors > 1 && (
              <span style={{ fontSize: '0.7rem', color: '#6b7280', marginLeft: '4px' }}>c/u</span>
            )}
          </span>
        </label>
      );
    })}
  </div>
)}

            {/* BOTÓN - deshabilitado si está cerrado */}
            <button 
  className={`movil-package-btn-grid ${isClosed ? 'movil-package-btn--disabled' : ''}`}
  style={{ 
    background: isClosed 
      ? '#6c757d' 
      : `linear-gradient(135deg, ${colors?.primaryColor || '#bc00dd'}, ${colors?.secondaryColor || '#00c6fb'})`,
    cursor: isClosed ? 'not-allowed' : 'pointer',
    opacity: isClosed ? 0.7 : 1
  }}
  onClick={() => {
    if (isClosed) return;
    const pkgAddons = selectedAddons[pkg.id_package || pkg.id] || {};
    const selectedAddonsMap = {};
    
    if (pkg.addons) {
      pkg.addons.forEach(addon => {
        const addonId = addon.id;
        // 🔥 Aseguramos que se pase como número entero (ej. 2 para duplas)
        selectedAddonsMap[addonId] = parseInt(pkgAddons[addonId]) || 0;
      });
    }
    
    // ✅ CORREGIDO: Asegurar que id_package esté definido
    const pkgWithAddons = {
      ...pkg,
      id_package: pkg.id_package || pkg.id,
      selectedAddons: selectedAddonsMap,
      totalAmount: calcularPrecioConAddons(pkg)
    };
    setSelectedPackage(pkgWithAddons);
    setTimeout(() => {
      setActiveTab('inscripcion');
    }, 50);
  }}
  disabled={isClosed}
>
  {isClosed ? '🔒 Agotado' : 'Seleccionar'}
</button>
          </div>
        );
      })}
    </div>
  </div>
)}

{packages.length === 0 && (
  <p className="movil-empty">No hay paquetes disponibles</p>
)}
{/* ==========================================
          BADGE DE PAGO 100% SEGURO CON LOGOS (MÓVIL)
          ========================================== */}
      <div className="movil-secure-payment-box">
        <div className="movil-secure-payment-content">
          <div className="movil-secure-header">
            <svg 
              className="movil-secure-icon" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <div className="movil-secure-text-group">
              <span className="movil-secure-title">PAGO 100% SEGURO</span>
              <span className="movil-secure-subtitle">Inscripción garantizada y procesada al instante</span>
            </div>
          </div>

          {/* Iconos de métodos de pago */}
          <div className="movil-payment-icons-row">
            {paymentMethods.map((icon, idx) => (
              <img 
                key={idx} 
                src={icon} 
                alt="Método de pago" 
                className="movil-payment-method-icon" 
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


// ==========================================
// MAP CONTROLLER (Copiado de PC)
// ==========================================
function MapController({ path }) {
  const map = useMap();

  useEffect(() => {
    if (path && path.length > 1) {
      const isMobile = window.innerWidth < 768;
      const paddingValue = isMobile ? 30 : 80;

      map.flyToBounds(path, {
        padding: [paddingValue, paddingValue],
        animate: true,
        duration: 1.2
      });
    } else if (path && path.length === 1) {
      map.flyTo(path[0], 15, { animate: true, duration: 1.2 });
    }
  }, [map, path]);

  return null;
}

// ==========================================
// ANIMATED GRADIENT POLYLINE (Copiado de PC)
// ==========================================
function AnimatedGradientPolyline({ positions, colors, weight }) {
  const [dashOffset, setDashOffset] = useState(1000);

  useEffect(() => {
    setDashOffset(1000);
    const timer = setTimeout(() => setDashOffset(0), 50);
    return () => clearTimeout(timer);
  }, [positions]);

  if (positions.length === 2) {
    return (
      <Polyline
        positions={positions}
        pathOptions={{
          color: colors[0],
          weight: weight,
          opacity: 0.9,
          dashArray: '1000',
          dashOffset: dashOffset.toString(),
          className: 'pc-animated-path-transition'
        }}
      />
    );
  }

  const segments = [];
  const steps = positions.length - 1;

  for (let i = 0; i < steps; i++) {
    const ratio = i / steps;
    const r = Math.round(parseInt(colors[0].slice(1, 3), 16) * (1 - ratio) + parseInt(colors[1].slice(1, 3), 16) * ratio);
    const g = Math.round(parseInt(colors[0].slice(3, 5), 16) * (1 - ratio) + parseInt(colors[1].slice(3, 5), 16) * ratio);
    const b = Math.round(parseInt(colors[0].slice(5, 7), 16) * (1 - ratio) + parseInt(colors[1].slice(5, 7), 16) * ratio);
    
    const segmentColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

    segments.push(
      <Polyline 
        key={`seg-${i}`} 
        positions={[positions[i], positions[i + 1]]} 
        pathOptions={{ 
          color: segmentColor, 
          weight: weight, 
          opacity: 0.9,
          dashArray: '1000',
          dashOffset: dashOffset.toString(),
          className: 'pc-animated-path-transition'
        }}
      />
    );
  }

  return <>{segments}</>;
}

// ==========================================================================
// MARCADORES MÓVIL ESTILO GARMIN (Círculos limpios idénticos a PC)
// ==========================================================================
const createMovilGarminMarker = (label, type, gradientColors) => {
  const color = type === 'start' ? gradientColors[0] : gradientColors[1];
  
  return L.divIcon({
    html: `
      <div class="movil-garmin-marker">
        <div class="movil-garmin-dot" style="background-color: ${color}; box-shadow: 0 0 8px ${color};"></div>
        <div class="movil-garmin-label" style="color: ${color}; font-weight: 700;">● ${label}</div>
      </div>
    `,
    className: 'movil-leaflet-garmin',
    iconSize: [80, 24],
    iconAnchor: [12, 12],
    popupAnchor: [23, -10]
  });
};

// ==========================================================================
// RENDER: RUTAS TAB MÓVIL (Sincronizado con lógica y degradados de PC)
// ==========================================================================
const renderRutasTab = () => {
  const gradients = [
    ["#bc00dd", "#00c6fb"],
    ["#ff416c", "#ffb347"],
    ["#00c853", "#64dd17"],
    ["#ff9800", "#ffd54f"],
    ["#3f51b5", "#00bcd4"],
    ["#e91e63", "#9c27b0"],
    ["#00acc1", "#26c6da"],
    ["#7c4dff", "#18ffff"]
  ];

  const currentGradient = gradients[activeRouteIndex % gradients.length];
  const activeRoute = routes[activeRouteIndex] || routes[0];

  // Procesamos la data del mapa actual usando la misma lógica limpia de PC
  const mapData = (() => {
    const defaultData = {
      start: [-16.5000, -68.1500],
      end: [-16.5000, -68.1500],
      path: [[-16.5000, -68.1500]],
      elevation_gain: null,
      elevation_loss: null,
      terrain: null,
      est_time: null
    };

    if (!activeRoute?.map_data) return defaultData;

    try {
      const parsed = typeof activeRoute.map_data === "string"
        ? JSON.parse(activeRoute.map_data)
        : activeRoute.map_data;

      return {
        start: parsed.start || defaultData.start,
        end: parsed.end || defaultData.end,
        path: parsed.path || [parsed.start || defaultData.start],
        elevation_gain: parsed.elevation_gain || null,
        elevation_loss: parsed.elevation_loss || null,
        terrain: parsed.terrain || null,
        est_time: parsed.est_time || null
      };
    } catch {
      return defaultData;
    }
  })();

  const isCircularCircuit = mapData.start[0] === mapData.end[0] && mapData.start[1] === mapData.end[1];

  const getDistanceBadge = (title) => {
    return title || "Ruta";
  };

  if (routes.length === 0) {
    return (
      <div className="movil-tab-content">
        <div className="movil-rutas-empty">
          <p>No hay rutas configuradas para este evento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="movil-tab-content" style={{ padding: 0, overflow: 'hidden', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      
      {/* DEFS SVG LOCALES PARA EL ICONO DEL CORREDOR */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
  <defs>
    {gradients.map(([start, end], i) => (
      <linearGradient
        key={i}
        id={`movil-gradient-${i}`}
        x1="0%"
        y1="0%"
        x2="100%"
        y2="0%"
      >
        <stop offset="0%" stopColor={start} />
        <stop offset="100%" stopColor={end} />
      </linearGradient>
    ))}
  </defs>
</svg>

      <div className="movil-tab-rutas-container">
        
        {/* TITULO SUPERIOR */}
        <div className="movil-routes-header">
          <span className="movil-routes-subtitle" style={{ color: currentGradient[0] }}>DISTANCIAS</span>
          <h2 className="movil-routes-title">Elige tu Distancia</h2>
        </div>

        {/* SELECTOR DE CARDS HORIZONTALES (Ultra compactas) */}
        <div className="movil-rutas-upper-cards">
          <div className="movil-rutas-scroll-stack">
            {routes.map((route, idx) => {
              const isActive = activeRouteIndex === idx;
              const distance = getDistanceBadge(route.title);
              const cleanTitle = route.title?.replace(/(\d+\s*K|\d+\s*k|-)/g, '').trim() || "Circuito";
              const gradient = gradients[idx % gradients.length];
              const gradientId = `movil-gradient-${idx % gradients.length}`;
              const cardGradientCSS = `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`;
              
              return (
                <div 
                  key={idx} 
                  className={`movil-route-selector-card ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveRouteIndex(idx)}
                  style={{ 
                    borderBottom: isActive ? `3px solid ${gradient[0]}` : '3px solid transparent'
                  }}
                >
                  
                  <div className="movil-route-card-dist" style={{
                    background: cardGradientCSS,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {distance}
                  </div>
                  <div className="movil-route-card-icon-wrapper">
            <FaRunning className="movil-route-card-icon-svg" style={{
              fill: `url(#${gradientId})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '3rem',
              display: 'block'
            }} />
          </div>
          <p className="movil-route-card-description">
  {route.description?.length > 65
    ? `${route.description.substring(0, 65)}...`
    : route.description}
</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CONTENEDOR DEL MAPA */}
        <div className="movil-rutas-lower-map" style={{ position: 'relative', flex: 1, width: '100%', overflow: 'hidden' }}>
          <MapContainer 
            center={mapData.start} 
            zoom={14} 
            zoomControl={false}
            scrollWheelZoom={true}
            style={{ width: '100%', height: '100%' }}
          >
            {/* Reutilizamos tu MapController responsivo de PC */}
            <MapController path={mapData.path} />

            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* ✅ DEGRADADO REAL EN EL MAPA: Reutilizamos tu componente unificado */}
            <AnimatedGradientPolyline 
              positions={mapData.path} 
              colors={currentGradient} 
              weight={5}
            />

            {/* ✅ MARCADORES ESTILO GARMIN FLOTANTES EN MAPA */}
            {isCircularCircuit ? (
              <Marker position={mapData.start} icon={createMovilGarminMarker('START / FINISH', 'start', currentGradient)}>
                <Popup><b>Circuito Cerrado</b></Popup>
              </Marker>
            ) : (
              <>
                <Marker position={mapData.start} icon={createMovilGarminMarker('START', 'start', currentGradient)}>
                  <Popup><b>Partida Oficial</b></Popup>
                </Marker>
                <Marker position={mapData.end} icon={createMovilGarminMarker('FINISH', 'end', currentGradient)}>
                  <Popup><b>Meta</b></Popup>
                </Marker>
              </>
            )}
          </MapContainer>

          {/* ⭐ TARJETA DE DESNIVELES FLOTANTE (ESTILO PC PERO PARA CELULAR) */}
          {(mapData.elevation_gain || mapData.elevation_loss || mapData.terrain || mapData.est_time) && (
            <div className="movil-map-floating-stats-card">
              <div className="movil-stats-scroll-row">
                
                <div className="movil-stat-item">
                  <span className="movil-stat-title">Distancia</span>
                  <div className="movil-stat-value-wrapper">
                    <FaRunning style={{ fill: 'url(#movil-active-gradient)' }} />
                    <span className="movil-stat-value">{getDistanceBadge(activeRoute?.title)}</span>
                  </div>
                </div>

                {mapData.elevation_gain && (
                  <div className="movil-stat-item">
                    <span className="movil-stat-title">Desnivel +</span>
                    <div className="movil-stat-value-wrapper">
                      <FaChevronUp style={{ color: '#00E676' }} />
                      <span className="movil-stat-value">+{mapData.elevation_gain}m</span>
                    </div>
                  </div>
                )}

                {mapData.elevation_loss && (
                  <div className="movil-stat-item">
                    <span className="movil-stat-title">Desnivel -</span>
                    <div className="movil-stat-value-wrapper">
                      <FaChevronDown style={{ color: '#FF1744' }} />
                      <span className="movil-stat-value">-{mapData.elevation_loss}m</span>
                    </div>
                  </div>
                )}

                {mapData.terrain && (
                  <div className="movil-stat-item">
                    <span className="movil-stat-title">Superficie</span>
                    <div className="movil-stat-value-wrapper">
                      <FaRoad style={{ color: '#00B5FF' }} />
                      <span className="movil-stat-value">{mapData.terrain}</span>
                    </div>
                  </div>
                )}

                {mapData.est_time && (
                  <div className="movil-stat-item">
                    <span className="movil-stat-title">Tiempo Est.</span>
                    <div className="movil-stat-value-wrapper">
                      <FaClock style={{ color: '#FFD54F' }} />
                      <span className="movil-stat-value">{mapData.est_time}</span>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* BOTÓN STRAVA COMPACTO */}
          {activeRoute?.link && (
            <a 
              href={activeRoute.link} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="movil-strava-floating-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l3.836 7.529h5.051L10.37.072 2.304 15.873h5.05" />
              </svg>
            </a>
          )}
        </div>

      </div>
    </div>
  );
};
  // ==========================================
  // RENDER: INSCRIPCIÓN TAB
  // ==========================================
  const renderInscripcionTab = useMemo(() => {
    // Solo se ejecuta cuando selectedPackage cambia
    
    
    return (
      <div className="movil-tab-content">
        <MovilFormulario 
          key={selectedPackage ? (selectedPackage.id_package || selectedPackage.id) : 'empty-form'}
          isEditable={false}
          selectedPackage={selectedPackage}
          isFillingForm={true}
          onTabChange={(tab) => {
            if (tab === 'competitors') {
              handleTabChange('participantes');
            } else {
              setActiveTab(tab);
            }
          }}
        />
      </div>
    );
  }, [selectedPackage]);
// ==========================================
// RENDER: PARTICIPANTES TAB (CON CAMPOS VISIBLES COMO PC - 2 LÍNEAS)
// ==========================================
const renderParticipantesTab = () => {
  if (isListOcultaPublic) {
    return (
      <div className="movil-tab-content privacy-locked">
        <span className="lock-icon">🔒</span>
        <h3>Lista de Participantes Privada</h3>
        <p>Por disposición de los organizadores del evento, el listado público no se encuentra disponible.</p>
      </div>
    );
  }

  const primaryAcc = colors?.primaryColor || '#bc00dd';
  const secondaryAcc = colors?.secondaryColor || '#00c6fb';

  // Helper para dorsal
  const getCompetitorDorsal = (comp) => {
    return comp.dorsal || comp.bib || comp.numero || null;
  };

  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toString()
      .trim()
      .replace(/^["']|["']$/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

// Helper para nombre completo flexible (soporta singular/plural, español/inglés y duplas/equipos)
const getFullName = (comp) => {
  if (!comp) return 'Atleta';

  const names = [];

  // ==========================================
  // UYUNI / EVENTOS CON CICLISTAS 1 Y 2
  // ==========================================

  const nombre1 = normalizeText(comp.nombre_ciclista_1 || '');
  const apellido1 = normalizeText(comp.apellido_ciclista_1 || '');

  if (nombre1 || apellido1) {
    names.push(`${nombre1} ${apellido1}`.trim());
  }

  const nombre2 = normalizeText(comp.nombre_ciclista_2 || '');
  const apellido2 = normalizeText(comp.apellido_ciclista_2 || '');

  if (nombre2 || apellido2) {
    names.push(`${nombre2} ${apellido2}`.trim());
  }

  // Si encontramos ciclistas, usamos solamente estos.
  // Así no se mezclan con la detección dinámica.
  if (names.length > 0) {
    return names.join(' / ');
  }

  // ==========================================
  // EVENTOS NORMALES
  // ==========================================

  const firstName = colKeys?.nombreCol
    ? normalizeText(comp[colKeys.nombreCol] || '')
    : '';

  const lastName = colKeys?.apellidoCol
    ? normalizeText(comp[colKeys.apellidoCol] || '')
    : '';

  const baseName = `${firstName} ${lastName}`.trim();

  if (baseName) {
    return baseName;
  }

  // ==========================================
  // FALLBACK DINÁMICO
  // ==========================================

  const nameKeywordsRegex =
    /(nombre|nombres|apellido|apellidos|atleta|atletas|participante|participantes|integrante|integrantes|pareja|parejas|competidor|competidores|runner|runners|rider|riders|biker|bikers|member|members|first_name|last_name|full_name|fullname|name|names)/i;

  const excludeRegex =
    /(club|equipo|team|categoria|category|paquete|package|dorsal|bib|numero|id|slug|status|estado|pago|monto|price|date|fecha)/i;

  const dynamicNames = [];

  Object.keys(comp).forEach((key) => {
    if (key === colKeys?.nombreCol || key === colKeys?.apellidoCol) return;
    if (excludeRegex.test(key)) return;

    if (nameKeywordsRegex.test(key) && comp[key]) {
      const val = normalizeText(comp[key]);

      if (val) {
        dynamicNames.push(val);
      }
    }
  });

  if (dynamicNames.length > 0) {
    return dynamicNames.join(' / ');
  }

  return 'Atleta';
};

  // Helper para club con columnas dinámicas
  const getClub = (comp) => {
    return colKeys.clubCol ? (comp[colKeys.clubCol] || 'Independiente') : 'Independiente';
  };

  // Detección de categoría
  const categoriaColLocal = (() => {
    const sample = competitors[0] || {};
    const keys = Object.keys(sample);
    return keys.find((k) => /categor/i.test(k)) || null;
  })();

  const getCompetitorCategory = (comp) => {
    if (!categoriaColLocal) return null;
    const rawCategory = comp[categoriaColLocal];
    return rawCategory ? rawCategory.toString().trim() : null;
  };

  // Helper para categoría completa
  const getFullCategory = (comp) => {
    const paquete = normalizeText(comp.paquete || comp.package || '');
    const categoria = normalizeText(getCompetitorCategory(comp) || '');
    
    if (paquete && categoria) return `${paquete} - ${categoria}`;
    if (categoria) return categoria;
    if (paquete) return `${paquete} - Sin Categoría`;
    return 'Sin Categoría';
  };

  // 🔍 Lógica de Filtrado por término de búsqueda
  const searchedCompetitors = competitors.filter((comp) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const fullName = getFullName(comp).toLowerCase();
    const fullCat = (getFullCategory(comp) || '').toLowerCase();
    const club = getClub(comp).toLowerCase();
    const bib = (comp.dorsal || comp.bib || comp.numero || '').toString();
    return (
      fullName.includes(term) ||
      fullCat.includes(term) ||
      club.includes(term) ||
      bib.includes(term)
    );
  });

  // ✅ Agrupar por PAQUETE + CATEGORÍA
  const competitorsByCategory = searchedCompetitors.reduce((acc, comp) => {
    const paquete = normalizeText(comp.paquete || comp.package || '').toString().trim();
    const rawCategory = normalizeText(getCompetitorCategory(comp) || '');
    
    let displayName = rawCategory ? capitalizeCategory(rawCategory) : 'Sin Categoría';
    if (paquete) {
      displayName = `${paquete} - ${displayName}`;
    }
    
    const normalizedKey = paquete && rawCategory 
      ? `${paquete} - ${rawCategory}`.toLowerCase()
      : (rawCategory || 'Sin Categoría');
    
    if (!acc[normalizedKey]) {
      acc[normalizedKey] = {
        displayName: displayName,
        competitors: []
      };
    }
    acc[normalizedKey].competitors.push(comp);
    return acc;
  }, {});

  const sortedCategories = Object.keys(competitorsByCategory).sort();
  const hasCategoryField = categoryColumnName !== null;

  // ✅ Helper para verificar si un campo es visible (misma lógica que PC)
  const isVisible = (colName) => {
    if (!visibleColumns || visibleColumns.length === 0) return true;
    return visibleColumns.some(col => 
      col.toLowerCase() === colName.toLowerCase()
    );
  };

  // ✅ Identificar qué columnas mostrar (respetando visibleColumns)
  const hasVisibleConfig = visibleColumns && visibleColumns.length > 0;
  
  const showDorsal = hasVisibleConfig 
    ? (isVisible('dorsal') || isVisible('bib') || isVisible('numero'))
    : true;
  
    const showName = hasVisibleConfig 
    ? (
        isVisible('nombre') ||
        isVisible('nombres') ||
        isVisible('apellido') ||
        isVisible('apellidos') ||
        isVisible('nombre_ciclista_1') ||
        isVisible('apellido_ciclista_1') ||
        isVisible('nombre_ciclista_2') ||
        isVisible('apellido_ciclista_2')
      )
    : true;
  
  const showCategory = hasVisibleConfig 
    ? (isVisible('categoria') || isVisible('category') || isVisible('paquete') || isVisible('categorias'))
    : true;
  
  const showClub = hasVisibleConfig 
    ? (isVisible('club') || isVisible('equipo') || isVisible('team') || isVisible('clubequipo'))
    : true;

  // 🆕 IDENTIFICAR CAMPOS SECUNDARIOS (LÍNEA 2) - IGUAL QUE PC
  const primaryKeys = [
    'dorsal', 'bib', 'numero',
    'nombre', 'name', 'first_name', 'apellido', 'lastname', 'last_name',
    'categoria', 'category', 'paquete', 'categorias',
    'club', 'equipo', 'team', 'clubequipo',
    'id', 'slug'
  ];

  // 🆕 Obtener columnas secundarias (las que no están en primaryKeys)
  const secondaryColumns = hasVisibleConfig
    ? visibleColumns.filter(col => 
        !primaryKeys.some(pKey => col.toLowerCase().includes(pKey.toLowerCase()))
      )
    : [];

  // 🆕 Función para obtener valor de columna secundaria
  const getSecondaryValue = (comp, colKey) => {
    return comp[colKey] || null;
  };

  return (
    <div className="movil-tab-content dark-sport-theme">
      <div className="movil-participants-header">
        <span className="movil-participants-subtitle" style={{ color: primaryAcc }}>
          START LIST OFICIAL
        </span>
        <h2 className="movil-participants-title">Atletas Inscritos</h2>
      </div>

      {/* 🔍 BARRA DE BÚSQUEDA */}
      <div className="movil-search-box-container" style={{ marginBottom: '16px' }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}>
          <span style={{
            position: 'absolute',
            left: '12px',
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.5)',
            pointerEvents: 'none'
          }}>🔍</span>

          <input
            type="text"
            placeholder="Buscar por nombre"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 36px 10px 36px',
              backgroundColor: 'rgba(255, 255, 255, 0.07)',
              border: `1.5px solid ${searchTerm ? secondaryAcc : 'rgba(255, 255, 255, 0.15)'}`,
              borderRadius: '10px',
              color: '#ffffff',
              fontSize: '13px',
              outline: 'none',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              boxShadow: searchTerm ? `0 0 10px ${secondaryAcc}33` : 'none'
            }}
          />

          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '10px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          )}
        </div>

        {searchTerm && (
          <div style={{
            fontSize: '12px',
            color: secondaryAcc,
            marginTop: '6px',
            paddingLeft: '4px',
            fontWeight: '600'
          }}>
            {searchedCompetitors.length} {searchedCompetitors.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}
          </div>
        )}
      </div>

      {/* ✅ TABS DE NAVEGACIÓN */}
      {hasCategoryField && (
        <div className="movil-competitors-tabs" style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
          borderBottom: `1px solid ${primaryAcc}40`,
          paddingBottom: '10px'
        }}>
          <button
            className={`movil-tab-btn ${competitorViewMode === 'general' ? 'active' : ''}`}
            onClick={() => setCompetitorViewMode('general')}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderBottom: competitorViewMode === 'general' ? `3px solid ${primaryAcc}` : '3px solid transparent',
              background: 'transparent',
              color: competitorViewMode === 'general' ? primaryAcc : 'rgba(255,255,255,0.6)',
              fontWeight: competitorViewMode === 'general' ? '700' : '500',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            General ({searchedCompetitors.length})
          </button>
          <button
            className={`movil-tab-btn ${competitorViewMode === 'categorias' ? 'active' : ''}`}
            onClick={() => setCompetitorViewMode('categorias')}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderBottom: competitorViewMode === 'categorias' ? `3px solid ${primaryAcc}` : '3px solid transparent',
              background: 'transparent',
              color: competitorViewMode === 'categorias' ? primaryAcc : 'rgba(255,255,255,0.6)',
              fontWeight: competitorViewMode === 'categorias' ? '700' : '500',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            Categorías
          </button>
        </div>
      )}

      {isLoadingCompetitors ? (
        <div className="movil-state-container">
          <p>Cargando lista de atletas...</p>
        </div>
      ) : competitorsError ? (
        <div className="movil-state-container error">
          <p>Error al cargar los participantes.</p>
          <small>{competitorsError}</small>
        </div>
      ) : searchedCompetitors.length > 0 ? (
        <div className="movil-competitors-list-wrapper">
          
          {/* ✅ VISTA GENERAL */}
          {competitorViewMode === 'general' && (
            <div className="movil-competitors-grid-stack">
              {searchedCompetitors.map((comp, index) => {
                const fullName = getFullName(comp);
                const club = getClub(comp);
                const fullCat = getFullCategory(comp);
                const dorsal = getCompetitorDorsal(comp);
                
                // 🆕 Obtener valores de campos secundarios
                const secondaryValues = secondaryColumns
                  .map(colKey => ({
                    key: colKey,
                    value: getSecondaryValue(comp, colKey)
                  }))
                  .filter(item => item.value !== null && item.value !== '');

                return (
                  <div
                    key={comp.id || index}
                    className="movil-competitor-row-wrapper"
                    style={{ '--accent-1': primaryAcc, '--accent-2': secondaryAcc }}
                  >
                    <div className="movil-trapezoid-body">
                      <div className="movil-competitor-text-container">
                        {/* 🆕 LÍNEA 1 */}
                        <div className="movil-line movil-line-primary">
                          {showDorsal && (
                            <span className="movil-competitor-dorsal">{dorsal || index + 1}</span>
                          )}
                          {showName && (
                            <span className="movil-competitor-fullname">{fullName}</span>
                          )}
                          {showCategory && hasCategoryField && fullCat && (
                            <span className="movil-competitor-category">
                              {capitalizeCategory(fullCat)}
                            </span>
                          )}
                          {showClub && (
                            <span className="movil-competitor-club">{club}</span>
                          )}
                        </div>

                        {/* 🆕 LÍNEA 2 - CAMPOS ADICIONALES */}
                        {secondaryValues.length > 0 && (
                          <div className="movil-line movil-line-secondary">
                            {secondaryValues.map((item, idx) => (
                              <span key={idx} className="movil-competitor-extra-field" title={`${item.key}: ${item.value}`}>
                                <span className="movil-field-label">{item.key}:</span> {item.value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="movil-external-lines">
                      <div className="line line-1" />
                      <div className="line line-2" />
                      <div className="line line-3" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ✅ VISTA POR CATEGORÍAS */}
          {competitorViewMode === 'categorias' && hasCategoryField && (
            <div className="movil-competitors-categories-stack" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {sortedCategories.map((normalizedKey) => {
                const group = competitorsByCategory[normalizedKey];
                if (!group.competitors.length) return null;
                return (
                  <div key={normalizedKey} className="movil-category-group-wrapper" style={{ marginBottom: '25px' }}>
                    <h4 style={{
                      color: primaryAcc,
                      fontSize: '16px',
                      fontWeight: '700',
                      marginBottom: '12px',
                      paddingBottom: '6px',
                      borderBottom: `2px solid ${primaryAcc}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>{group.displayName}</span>
                      <span style={{
                        fontSize: '12px',
                        background: `${secondaryAcc}25`,
                        color: secondaryAcc,
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontWeight: '600'
                      }}>
                        {group.competitors.length}
                      </span>
                    </h4>
                    <div className="movil-competitors-grid-stack">
                      {group.competitors.map((comp, index) => {
                        const fullName = getFullName(comp);
                        const club = getClub(comp);
                        const dorsal = getCompetitorDorsal(comp);
                        
                        // 🆕 Obtener valores de campos secundarios
                        const secondaryValues = secondaryColumns
                          .map(colKey => ({
                            key: colKey,
                            value: getSecondaryValue(comp, colKey)
                          }))
                          .filter(item => item.value !== null && item.value !== '');

                        return (
                          <div
                            key={comp.id || index}
                            className="movil-competitor-row-wrapper"
                            style={{ '--accent-1': primaryAcc, '--accent-2': secondaryAcc }}
                          >
                            <div className="movil-trapezoid-body">
                              <div className="movil-competitor-text-container">
                                {/* 🆕 LÍNEA 1 */}
                                <div className="movil-line movil-line-primary">
                                  {showDorsal && (
                                    <span className="movil-competitor-dorsal">{dorsal || index + 1}</span>
                                  )}
                                  {showName && (
                                    <span className="movil-competitor-fullname">{fullName}</span>
                                  )}
                                  {showClub && (
                                    <span className="movil-competitor-club">{club}</span>
                                  )}
                                </div>

                                {/* 🆕 LÍNEA 2 - CAMPOS ADICIONALES */}
                                {secondaryValues.length > 0 && (
                                  <div className="movil-line movil-line-secondary">
                                    {secondaryValues.map((item, idx) => (
                                      <span key={idx} className="movil-competitor-extra-field" title={`${item.key}: ${item.value}`}>
                                        <span className="movil-field-label">{item.key}:</span> {item.value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="movil-external-lines">
                              <div className="line line-1" />
                              <div className="line line-2" />
                              <div className="line line-3" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="movil-state-container empty">
          <p>{searchTerm ? 'No se encontraron atletas con esa búsqueda.' : 'No hay competidores inscritos aún.'}</p>
          {!searchTerm && <span>¡Sé el primero en registrarte!</span>}
        </div>
      )}
    </div>
  );
};
// ==========================================
// RENDER: MÁS TAB (Kit del Corredor & Organización)
// ==========================================
const renderMasTab = () => {
  // 🆕 OBTENER schedule_data de eventData (featured_events)
  const scheduleData = eventData?.scheduleData;
  
  // Fallback: calcular fechas desde templateData
  const mainEventDateStr = templateData?.end_date || templateData?.event_date || eventData?.endDate;
  
  const getFormattedDate = (baseDateStr, daysToSubtract = 0) => {
    if (!baseDateStr) return "Por confirmar";
    try {
      const date = new Date(baseDateStr);
      if (isNaN(date.getTime())) return "Por confirmar";
      if (daysToSubtract > 0) {
        date.setDate(date.getDate() - daysToSubtract);
      }
      const options = { weekday: 'long', day: 'numeric', month: 'short' };
      let formatted = date.toLocaleDateString('es-ES', options);
      return formatted.charAt(0).toUpperCase() + formatted.slice(1).replace('.', '');
    } catch (e) {
      return "Por confirmar";
    }
  };
  
  const kitDate = getFormattedDate(mainEventDateStr, 2);
  const raceDayDate = getFormattedDate(mainEventDateStr, 0);
  
  // Fallback si no hay schedule_data
  const fallbackSchedule = [
    { id: 1, badge: "Días Previos", title: "Entrega de Dorsales", icon: "FaShoppingBag", date: kitDate, time: "10:00 - 18:00" },
    { id: 2, badge: "Día del Evento", title: "Concentración", icon: "FaUsers", date: raceDayDate, time: "06:30 AM" },
    { id: 3, badge: "Día del Evento", title: "Largada Oficial", icon: "FaFlag", date: raceDayDate, time: "07:30 AM" },
    { id: 4, badge: "Día del Evento", title: "Cierre de Meta", icon: "FaFlagCheckered", date: raceDayDate, time: "11:00 AM" },
    { id: 5, badge: "Día del Evento", title: "Premiación", icon: "FaTrophy", date: raceDayDate, time: "11:30 AM" }
  ];
  
  // 🎯 USAR scheduleData si existe
  const finalSchedule = (scheduleData && Array.isArray(scheduleData) && scheduleData.length > 0)
    ? scheduleData
    : fallbackSchedule;
  
  // Mapeo de iconos
  const scheduleIconMap = {
    'FaShoppingBag': FaShoppingBag,
    'FaUsers': FaUsers,
    'FaFlag': FaFlag,
    'FaFlagCheckered': FaFlagCheckered,
    'FaTrophy': FaTrophy,
    'FaClock': FaClock
  };
  
  const primaryColor = colors?.primaryColor || '#bc00dd';
  const secondaryColor = colors?.secondaryColor || '#00c6fb';
  const gradientStyle = {
    background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
  };
  
  return (
    <div className="movil-tab-mas">
      <div className="movil-schedule-section">
        <div className="movil-section-header">
          <span className="movil-section-badge" style={{ color: primaryColor }}>CALENDARIO</span>
          <h3 className="movil-section-title">Cronograma del Evento</h3>
        </div>
        <div className="movil-timeline">
          {finalSchedule.map((item, index) => {
            const IconComponent = scheduleIconMap[item.icon] || FaClock;
            const isLast = index === finalSchedule.length - 1;
            
            return (
              <div key={item.id || index} className={`movil-timeline-item ${isLast ? 'movil-timeline-last' : ''}`}>
                <div className="movil-timeline-icon" style={gradientStyle}>
                  <IconComponent className="movil-tl-icon" />
                </div>
                <div className="movil-timeline-connector">
                  <div className={`movil-tl-line ${isLast ? 'movil-tl-line-last' : ''}`}></div>
                </div>
                <div className="movil-timeline-content">
                  {item.badge && <span className="movil-tl-badge">{item.badge}</span>}
                  <h4 className="movil-tl-title">{item.title}</h4>
                  <p className="movil-tl-date">{item.date}</p>
                  <p className="movil-tl-time">⏰ {item.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ==========================================
          SECCIÓN 2: KIT DEL CORREDOR
          ========================================== */}
      {images.length > 0 && (
        <div className="movil-kit-section">
          <div className="movil-section-header">
            <span className="movil-section-badge" style={{ color: primaryColor }}>INCLUIDO</span>
            <h3 className="movil-section-title">Galeria</h3>
          </div>
          
          <p className="movil-section-description">
            Presenta tu C.I. el día de la entrega para recibir tu equipamiento oficial.
          </p>

          <div className="movil-kit-grid">
            {images.slice(0, 6).map((src, index) => (
              <div key={index} className="movil-kit-item">
                <img src={src} alt={`Kit item ${index + 1}`} className="movil-kit-image" />
                <div className="movil-kit-overlay">
                  <span className="movil-kit-number">#{index + 1}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==========================================
          SECCIÓN 3: CONTACTO
          ========================================== */}
      <div className="movil-contact-section">
        <div className="movil-section-header">
          <span className="movil-section-badge" style={{ color: primaryColor }}>CONTACTO</span>
          <h3 className="movil-section-title">Organización del Evento</h3>
        </div>

        <p className="movil-section-description">
          ¿Tienes dudas sobre categorías, pagos o logística? Escríbenos por WhatsApp.
        </p>

        {telefono ? (
          <a 
            href={`https://wa.me/${telefono}`} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="movil-whatsapp-btn"
          >
            <FaWhatsapp className="movil-whatsapp-icon" />
            <div className="movil-whatsapp-info">
              <span className="movil-wa-label">Contactar por WhatsApp</span>
              <span className="movil-wa-number">{telefono}</span>
            </div>
            <span className="movil-wa-arrow">→</span>
          </a>
        ) : (
          <div className="movil-no-contact">
            <span>📱 Contacto disponible el día del evento</span>
          </div>
        )}
      </div>

    </div>
  );
};

  // ==========================================
  // RENDER: TAB CONTENT
  // ==========================================
  const renderTabContent = () => {
    switch (activeTab) {
      case 'info': return renderInfoTab();
      case 'rutas': return renderRutasTab();
      case 'inscripcion': return renderInscripcionTab;
      case 'participantes': return renderParticipantesTab();
      case 'mas': return renderMasTab();
      default: return renderInfoTab();
    }
  };

 // ==========================================
// RENDER: TAB BAR INFERIOR (CON BOTÓN CENTRAL DESTACADO)
// ==========================================
const renderTabBar = () => {
  // Definir tabs con la propiedad 'isMain' para el del medio
  const tabsConfig = [
    { id: 'info', icon: <FaInfoCircle />, label: 'Info' },
    { id: 'rutas', icon: <FaMap />, label: 'Rutas' },
    { id: 'inscripcion', icon: <FaClipboardList />, label: 'Inscripción', isMain: true },
    { id: 'participantes', icon: <FaUsers />, label: 'Participantes' },
    { id: 'mas', icon: <FaEllipsisH />, label: 'Más' },
  ];

  return (
    <div className="movil-tab-bar" style={{ 
      backgroundColor: colors?.secondaryColor || '#123a61',
      borderTop: `1px solid ${colors?.primaryColor || '#9370DB'}40`
    }}>
      {tabsConfig.map((tab) => {
        const isActive = activeTab === tab.id;
        const isMain = tab.isMain;

        // BOTÓN PRINCIPAL (el del medio) - Círculo grande
        if (isMain) {
          return (
            <button
              key={tab.id}
              className={`movil-tab-main ${isActive ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="movil-tab-main-icon">
                {/* Definición del degradado SVG inyectada localmente para que no falle la referencia */}
                <svg width="0" height="0" style={{ position: 'absolute' }}>
                  <linearGradient id="tab-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#bc00dd" />
                    <stop offset="100%" stopColor="#00c6fb" />
                  </linearGradient>
                </svg>
                {tab.icon}
              </span>
              <span className="movil-tab-main-label">{tab.label}</span>
            </button>
          );
        }

        // TABS NORMALES
        return (
          <button
            key={tab.id}
            className={`movil-tab-item ${isActive ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            style={{
              color: isActive ? colors?.primaryColor : 'rgba(255,255,255)'
            }}
          >
            <span className="movil-tab-icon">{tab.icon}</span>
            <span className="movil-tab-label">{tab.label}</span>
            {isActive && (
              <span className="movil-tab-indicator" style={{ backgroundColor: colors?.primaryColor }} />
            )}
          </button>
        );
      })}
    </div>
  );
};



  // ==========================================
  // LOADING / ERROR
  // ==========================================
  if (isLoading) {
    return (
      <div className="movil-loading">
        <div className="movil-loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (!templateData) {
    return <div className="movil-error">No se encontró el evento</div>;
  }

  // ==========================================
  // RENDER PRINCIPAL
  // ==========================================
  return (
    <div className="movil-layout">


      {/* Contenido según tab */}
      <div className="movil-content">
        {renderTabContent()}
      </div>

      {/* Tab Bar inferior */}
      {renderTabBar()}
    </div>
  );
};

export default MovilLayout;